import React, { useState, useMemo } from 'react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { MultiSelectFilter } from '@/components/pulse-operations/MultiSelectFilter';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Filter, X, ChevronDown } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'not_interested', label: 'Not Interested' }
];

const VERIFICATION_OPTIONS = [
  { value: 'unverified', label: 'Unverified' },
  { value: 'tl_only', label: 'TL Verified' },
  { value: 'mgr_only', label: 'Mgr Verified' },
  { value: 'both', label: 'Both Verified' }
];

const MOVE_OPTIONS = [
  { value: 0, label: 'No moves' },
  { value: 1, label: '1 move' },
  { value: 2, label: '2 moves' },
  { value: 3, label: '3 moves' }
];

const CommitmentFilterBar = ({ filters, setFilters, options, activeFilterCount }) => {
  const [open, setOpen] = useState(false);

  const clearAllFilters = () => {
    setFilters({
      gyms: [],
      tls: [],
      managers: [],
      statuses: [],
      verificationStatuses: [],
      dateRange: { from: null, to: null },
      amountRange: { min: null, max: null },
      moveCounts: []
    });
  };

  const handleMoveToggle = (value) => {
    const current = filters.moveCounts || [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setFilters(prev => ({ ...prev, moveCounts: next }));
  };

  return (
    <Card className="mb-4">
      <CardContent className="py-2 px-4">
        <Collapsible open={open} onOpenChange={setOpen}>
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2 h-8 px-2">
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium">Filters</span>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    {activeFilterCount}
                  </Badge>
                )}
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-3 w-3 mr-1" />
                Clear All ({activeFilterCount})
              </Button>
            )}
          </div>

          <CollapsibleContent>
            <div className="mt-3 pt-3 border-t space-y-3">
              {/* Row 1: Gym, TL, Manager, Status */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MultiSelectFilter
                  label="Gym"
                  options={options?.gyms || []}
                  selected={filters.gyms || []}
                  onChange={(v) => setFilters(prev => ({ ...prev, gyms: v }))}
                />
                <MultiSelectFilter
                  label="Team Leader"
                  options={options?.tls || []}
                  selected={filters.tls || []}
                  onChange={(v) => setFilters(prev => ({ ...prev, tls: v }))}
                />
                <MultiSelectFilter
                  label="Manager"
                  options={options?.managers || []}
                  selected={filters.managers || []}
                  onChange={(v) => setFilters(prev => ({ ...prev, managers: v }))}
                />
                <MultiSelectFilter
                  label="Status"
                  options={STATUS_OPTIONS}
                  selected={filters.statuses || []}
                  onChange={(v) => setFilters(prev => ({ ...prev, statuses: v }))}
                />
              </div>

              {/* Row 2: Verification, Date Range, Amount Range, Movement History */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MultiSelectFilter
                  label="Verification"
                  options={VERIFICATION_OPTIONS}
                  selected={filters.verificationStatuses || []}
                  onChange={(v) => setFilters(prev => ({ ...prev, verificationStatuses: v }))}
                />

                {/* Date Range */}
                <div className="flex flex-col gap-1 min-w-[200px]">
                  <Label className="text-sm font-medium text-gray-700">Date Range</Label>
                  <div className="flex gap-1">
                    <Input
                      type="date"
                      placeholder="From"
                      value={filters.dateRange?.from || ''}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, from: e.target.value || null }
                      }))}
                      className="h-9 text-xs"
                    />
                    <Input
                      type="date"
                      placeholder="To"
                      value={filters.dateRange?.to || ''}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, to: e.target.value || null }
                      }))}
                      className="h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Amount Range */}
                <div className="flex flex-col gap-1 min-w-[200px]">
                  <Label className="text-sm font-medium text-gray-700">Amount Range</Label>
                  <div className="flex gap-1">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={filters.amountRange?.min ?? ''}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        amountRange: { ...prev.amountRange, min: e.target.value === '' ? null : Number(e.target.value) }
                      }))}
                      className="h-9 text-xs"
                      min={0}
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={filters.amountRange?.max ?? ''}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        amountRange: { ...prev.amountRange, max: e.target.value === '' ? null : Number(e.target.value) }
                      }))}
                      className="h-9 text-xs"
                      min={0}
                    />
                  </div>
                </div>

                {/* Movement History */}
                <div className="flex flex-col gap-1 min-w-[200px]">
                  <Label className="text-sm font-medium text-gray-700">Movement History</Label>
                  <div className="flex items-center gap-3 h-9 pt-1">
                    {MOVE_OPTIONS.map((opt) => (
                      <div key={opt.value} className="flex items-center gap-1">
                        <Checkbox
                          id={`move-${opt.value}`}
                          checked={(filters.moveCounts || []).includes(opt.value)}
                          onCheckedChange={() => handleMoveToggle(opt.value)}
                        />
                        <label htmlFor={`move-${opt.value}`} className="text-xs text-gray-600 cursor-pointer">
                          {opt.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default CommitmentFilterBar;
